import { useEffect } from 'react';

/**
 * Blocks SPA navigation (pushState, back/forward) with a confirm dialog.
 * Works with BrowserRouter — does NOT require a data router.
 * Also blocks browser close/refresh via beforeunload.
 *
 * Note: Only intercepts pushState (used by navigate/Link). replaceState is
 * intentionally left alone — React Router and other code call it for
 * non-navigation URL updates (hash changes, query param cleanup).
 */
export function useNavigationBlock(shouldBlock: boolean, message: string) {
  useEffect(() => {
    if (!shouldBlock) return;

    // Track the URL we're protecting so we can restore it on cancelled back/forward
    let protectedUrl = window.location.href;
    let suppressPopstate = false;

    // Block browser close/refresh
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Override pushState to intercept SPA forward navigation
    const originalPushState = history.pushState.bind(history);

    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      if (!window.confirm(message)) return;
      originalPushState(...args);
      protectedUrl = window.location.href;
    };

    // Block back/forward button
    const handlePopState = () => {
      if (suppressPopstate) {
        suppressPopstate = false;
        return;
      }
      if (!window.confirm(message)) {
        // Restore the URL we were on before the back/forward navigation
        originalPushState(null, '', protectedUrl);
        // Fire synthetic popstate so React Router re-renders at the restored URL
        suppressPopstate = true;
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        protectedUrl = window.location.href;
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
    };
  }, [shouldBlock, message]);
}
